// @flow
import Popper, { type PopperOptions } from 'popper.js';
import React, { createElement, createRef, Component, type Node } from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';

import ModalRoot from './ModalRoot';

const placements = {
  auto: 'auto',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
  top: 'top',
  'auto-end': 'auto-end',
  'auto-start': 'auto-start',
  'bottom-end': 'bottom-end',
  'bottom-start': 'bottom-start',
  'left-end': 'left-end',
  'left-start': 'left-start',
  'right-end': 'right-end',
  'right-start': 'right-start',
  'top-end': 'top-end',
  'top-start': 'top-start',
};

const styleTypes = {
  light: {
    backgroundColor: colors.white,
    boxShadow:
      '0 1px 10px 0 rgba(0, 0, 0, 0.1), 0 4px 5px 0 rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.16)',
    color: colors.charcoal,
    padding: '12px 16px',
    tailBorder: '11px solid transparent',
    tailFilter: 'drop-shadow(0px -4px 3px rgba(0, 0, 0, 0.05))',
    tailMargin: '8px',
    tailPosition: '-18px',
  },
  dark: {
    backgroundColor: colors.charcoal,
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    color: colors.white,
    padding: '8px 12px;',
    tailBorder: '7px solid transparent',
    tailFilter: 'drop-shadow(0px 4px 2px rgba(0, 0, 0, 0.16))',
    tailMargin: '5px',
    tailPosition: '-11px',
  },
};

const Bubble = styled.div`
  background-color: ${({ styleType }) => styleTypes[styleType].backgroundColor};
  border-radius: 8px;
  box-shadow: ${({ styleType }) => styleTypes[styleType].boxShadow};
  color: ${({ styleType }) => styleTypes[styleType].color};
  font-size: 12px;
  padding: ${({ styleType }) => styleTypes[styleType].padding};
  position: relative;
`;

const TailPositionWrapper = styled.div`
  position: absolute;

  // tailPosition is calculated using half of the width of the Tail component
  // plus the width of the tailMargin on one side
  [x-placement|='bottom'] & {
    top: ${({ styleType }) => styleTypes[styleType].tailPosition};
  }
  [x-placement|='left'] & {
    right: ${({ styleType }) => styleTypes[styleType].tailPosition};
    transform: rotate(90deg);
  }
  [x-placement|='right'] & {
    left: ${({ styleType }) => styleTypes[styleType].tailPosition};
    transform: rotate(270deg);
  }
  [x-placement|='top'] & {
    bottom: ${({ styleType }) => styleTypes[styleType].tailPosition};
    transform: rotate(180deg);
  }
`;

const Tail = styled.div`
  // It's easier to style the tail when it's a square because then all the transformations are
  // symmetrical. This div has zero height and zero width. All 4 borders meet each other at
  // a 45 degree angle.
  border: ${({ styleType }) => styleTypes[styleType].tailBorder};
  border-bottom-color: ${({ styleType }) => styleTypes[styleType].backgroundColor};
  filter: ${({ styleType }) => styleTypes[styleType].tailFilter};
`;

const BubbleWrapper = styled.div`
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;

type TooltipProps = {
  shouldUpdateOnOpen: boolean,
  children: Node,
  tag: string,
  tooltipContent: Node,
  styleType: string,
  isAllowedToOverflowParent: boolean,
  placement: $Values<typeof placements>,
  inlineFlex: boolean,
};

type TooltipState = {
  isOpen: boolean,
};

/**
 * Tooltip wraps the anchor element and handles displaying the popup when the user
 * hovers or clicks on the anchor.
 */
class Tooltip extends Component<TooltipProps, TooltipState> {
  static defaultProps = {
    isAllowedToOverflowParent: false,
    placement: 'bottom',
    styleType: 'light',
    tag: 'div',
    shouldUpdateOnOpen: false,
    inlineFlex: true,
  };

  state = {
    isOpen: false,
  };

  componentDidMount = () => {
    const options: PopperOptions = {
      modifiers: {
        arrow: {
          element: this.arrowRef.current,
          enabled: true,
        },
        offset: {
          enabled: true,
        },
      },
    };
    if (this.props.placement) {
      options.placement = this.props.placement;
    }
    if (this.props.isAllowedToOverflowParent) {
      options.modifiers.hide = { enabled: false };
      options.modifiers.preventOverflow = { enabled: false, padding: 0 };
    }
    if (this.props.styleType === 'dark') {
      options.modifiers.offset.offset = '0, 4px';
    } else {
      options.modifiers.offset.offset = '0, 8px';
    }

    this.popper = new Popper(this.anchorRef.current, this.popupRef.current, options);
    if (this.popper) {
      this.popper.update();
    }
  };

  componentWillUnmount = () => {
    if (this.popper) {
      this.popper.destroy();
    }
    delete this.popper;
  };

  popper: ?typeof Popper;

  anchorRef = createRef();
  arrowRef = createRef();
  popupRef = createRef();

  openTooltip = () => {
    // shouldUpdateOnOpen is used when poppers anchor position could have changed and needs to update,
    // but not due to a window resize or scroll
    const { shouldUpdateOnOpen } = this.props;
    if (shouldUpdateOnOpen && this.popper) {
      this.popper.update();
    }
    this.setState({ isOpen: true });
  };

  closeTooltip = () => {
    this.setState({ isOpen: false });
  };

  render() {
    const { isOpen } = this.state;
    const { children, tag, tooltipContent, styleType, inlineFlex } = this.props;

    return createElement(
      tag,
      {
        ref: this.anchorRef,
        onClick: this.openTooltip,
        onMouseEnter: this.openTooltip,
        onMouseLeave: this.closeTooltip,
        style: inlineFlex ? { display: 'inline-flex' } : {},
      },
      children,
      <ModalRoot>
        <BubbleWrapper ref={this.popupRef} isVisible={isOpen}>
          <Bubble styleType={styleType}>{tooltipContent}</Bubble>
          <TailPositionWrapper ref={this.arrowRef} styleType={styleType}>
            <Tail styleType={styleType} />
          </TailPositionWrapper>
        </BubbleWrapper>
      </ModalRoot>,
    );
  }
}

export default Tooltip;
